import { Box, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import fontWeight from "../../../styles/mui/fontWeight";
import {
  GridContainer,
  IndicatorGrid,
  PerformanceCriteriaGrid,
  PerformanceIndicatorGrid,
  ScoreGridStyle,
  TotalScoreContainer,
  TotalScoreGrid,
} from "../styles/style";
import {
  generatePieChartData,
  ScorecardConstants,
} from "../../../constants/Scorecard";
import {
  KeyPerformanceIndicator,
  PerformanceCriteriaDetails,
} from "../../../models/Scorecard";
import { formatPercentageValue, length } from "../../../utils";
import CustomPieChart from "./CustomPieChart";

export interface PropsFromState {
  performanceCriteriaData: PerformanceCriteriaDetails;
  isErrorAvailable: boolean;
}

type AllProps = PropsFromState;

const PerformanceCriteria: React.FC<AllProps> = ({
  performanceCriteriaData,
  isErrorAvailable
}) => {

  return (
    <Grid container sx={GridContainer}>
      <Grid size={{ xs: 7 }} sx={PerformanceCriteriaGrid}>
        <Box>
          {renderDetails(ScorecardConstants.PERFORMANCE_CRITERIA)}
          <Box sx={IndicatorGrid}>
            {renderPerformanceIndicatorGrid(
              performanceCriteriaData.performanceCriteriaName,
              0,
              performanceCriteriaData.branchScoreTotal,
              performanceCriteriaData.maxScoreTotal,
              true,
              true,
              isErrorAvailable
            )}
          </Box>
          {renderScoreGrid()}
          {performanceCriteriaData &&
            length(performanceCriteriaData.keyPerformanceIndicator) &&
            performanceCriteriaData.keyPerformanceIndicator.map(
              (list: KeyPerformanceIndicator) => (
                <Box key={list.indicatorName}>
                  {renderPerformanceIndicatorGrid(
                    list.indicatorName,
                    list.branchScore,
                    list.pointsEarned,
                    list.maxScore,
                    false,
                    false,
                    isErrorAvailable
                  )}
                </Box>
              )
            )}
        </Box>
      </Grid>
      <Grid size={{ xs: 5 }} sx={TotalScoreGrid}>
        <Grid container sx={TotalScoreContainer}>
          <Grid size={{ xs: 4 }} p={"1rem 0"}>
            {renderTotalScoreBox(
              ScorecardConstants.WEIGHTED_SCORE,
              formatPercentageValue(performanceCriteriaData.weightedScore),
              isErrorAvailable
            )}
            <Box pt={"3rem"}>
              {renderTotalScoreBox(
                ScorecardConstants.CRITERIA_WEIGHT,
                formatPercentageValue(performanceCriteriaData.criteriaWeight),
                isErrorAvailable
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 5.2 }}>
            {!isErrorAvailable && <><CustomPieChart
              data={generatePieChartData(
                performanceCriteriaData.progressScoreName,
                performanceCriteriaData.pieScore,
              )}
            />
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight[3]}
                pl={"1.3rem"}
              >
                {performanceCriteriaData.progressScoreName}
              </Typography>
            </>}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const renderDetails = (
  label: string | number,
  align?: any,
  isBold?: boolean,
  isTotalScore?: boolean
) => {
  const typographyVariant = isTotalScore && isBold 
  ? "h2" 
  : isBold 
  ? "h6" 
  : "subtitle2";
  return (
    <Typography
      variant={typographyVariant}
      fontWeight={isBold ? fontWeight[5] : fontWeight[3]}
      align={align}
    >
      {label}
    </Typography>
  );
};

const renderScoreGrid = () => {
  return (
    <Grid container sx={ScoreGridStyle}>
      <Grid size={{ xs: 6.3 }}>
        {renderDetails(ScorecardConstants.KEY_PERFORMANCE_INDICATORS)}
      </Grid>
      <Grid size={{ xs: 2 }}>
        {renderDetails(ScorecardConstants.BRANCH_SCORE, "right")}
      </Grid>
      <Grid size={{ xs: 2 }}>
        {renderDetails(ScorecardConstants.POINTS_EARNED, "right")}
      </Grid>
      <Grid size={{ xs: 1.7 }}>
        {renderDetails(ScorecardConstants.MAX_POINTS, "right")}
      </Grid>
    </Grid>
  );
};

const renderPerformanceIndicatorGrid = (
  indicatorName: string,
  branchScore: number | string,
  pointsEarned: number,
  maxScore: number,
  isBold?: boolean,
  isTotalScore?: boolean,
  isErrorAvailable?: boolean
) => {

  return (
    <Grid container sx={PerformanceIndicatorGrid}>
      <Grid size={{ xs: 6.4 }}>
        {renderDetails(indicatorName, "left", isBold)}
      </Grid>
      <Grid size={{ xs: 2 }}>
        { !isTotalScore && renderDetails(isErrorAvailable ? "-" : branchScore, "right", isBold, isTotalScore)}
      </Grid>
      <Grid size={{ xs: 2 }}>
        {renderDetails(isErrorAvailable ? "-" : pointsEarned, "right", isBold, isTotalScore)}
      </Grid>
      <Grid size={{ xs: 1.6 }}>
        {renderDetails(isErrorAvailable ? "-" : maxScore, "right", isBold, isTotalScore)}
      </Grid>
    </Grid>
  );
};

const renderTotalScoreBox = (label: string, value: number | string, isErrorAvailable: boolean) => {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={fontWeight[3]} align="right">
        {label}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={fontWeight[6]}
        align="right"
        pt={"0.7rem"}
      >
        {isErrorAvailable ? "-" : value}
      </Typography>
    </Box>
  );
};

export default PerformanceCriteria;
